import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertTriangle, Loader2, RefreshCw, ChevronRight, Calendar, User,
  GraduationCap, ExternalLink, Check, X, Ban, MoreVertical, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = "/api";

const TAG_OPTIONS = [
  { value: "present", label: "Present", icon: Check, className: "text-emerald-700 focus:bg-emerald-50", confirm: "Tag as Present? 1 credit will be deducted from the student." },
  { value: "no_show", label: "No Show", icon: X, className: "text-amber-700 focus:bg-amber-50", confirm: "Tag as No Show? 1 credit will still be deducted." },
  { value: "cancelled_by_student", label: "Cancelled by Student", icon: Ban, className: "text-slate-700 focus:bg-slate-50", confirm: "Mark as Cancelled by Student? No credit will be deducted." },
  { value: "cancelled_by_teacher", label: "Cancelled by Teacher", icon: Ban, className: "text-slate-700 focus:bg-slate-50", confirm: "Mark as Cancelled by Teacher? No credit will be deducted." },
];

function severityForDays(days) {
  if (days >= 14) return { label: "Very Overdue", className: "bg-rose-100 text-rose-800 border-rose-300" };
  if (days >= 7)  return { label: "Overdue",       className: "bg-amber-100 text-amber-800 border-amber-300" };
  if (days >= 3)  return { label: "Pending",       className: "bg-yellow-100 text-yellow-800 border-yellow-300" };
  return                  { label: "Recent",        className: "bg-slate-100 text-slate-700 border-slate-300" };
}

/** Inline dropdown to quick-tag a single slot. Calls onTagged(slot_id, new_status). */
function QuickTagMenu({ slot, onTagged, busy }) {
  const handleTag = async (opt) => {
    if (!window.confirm(`${opt.confirm}\n\nClass: ${slot.date} ${slot.time}\nStudent: ${slot.student_name}\nTutor: ${slot.tutor_name}`)) return;
    await onTagged(slot.slot_id, opt.value, opt.label);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          className="h-8 gap-1 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
          onClick={(e) => e.stopPropagation()}
          data-testid={`quick-tag-trigger-${slot.slot_id}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MoreVertical className="w-3.5 h-3.5" />}
          Quick Tag
          <ChevronDown className="w-3 h-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuLabel className="text-xs text-slate-500">Tag this class as…</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {TAG_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          return (
            <DropdownMenuItem
              key={opt.value}
              className={cn("cursor-pointer text-sm gap-2", opt.className)}
              onSelect={() => handleTag(opt)}
              data-testid={`quick-tag-${opt.value}-${slot.slot_id}`}
            >
              <Icon className="w-4 h-4" />
              {opt.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AdminUntaggedAlerts() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [daysBack, setDaysBack] = useState(30);
  const [busyId, setBusyId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/untagged-past-classes?days_back=${daysBack}`, {
        headers: getAuthHeader(),
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, [daysBack, getAuthHeader]);

  useEffect(() => {
    fetchData();
    // poll every 5 min to keep the panel fresh without forcing a manual refresh
    const t = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchData]);

  const tagSlot = useCallback(async (slotId, status, label) => {
    setBusyId(slotId);
    try {
      await axios.put(
        `${API}/schedule/slots/${slotId}/tag`,
        { status, notes: `Tagged via Untagged Alerts panel (${label})` },
        { headers: getAuthHeader() }
      );
      toast.success(`Class tagged as ${label}`);
      // Optimistically remove from local list while we re-fetch
      setData((prev) => {
        if (!prev) return prev;
        const items = (prev.items || []).filter((it) => it.slot_id !== slotId);
        return { ...prev, items, total_untagged: Math.max(0, (prev.total_untagged || 0) - 1) };
      });
      // Re-fetch to refresh summary aggregations
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to tag class");
    } finally {
      setBusyId(null);
    }
  }, [getAuthHeader, fetchData]);

  const items = data?.items || [];
  const summary = data?.summary_by_tutor || [];
  const total = data?.total_untagged || 0;

  return (
    <div className="space-y-6" data-testid="admin-untagged-alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="alerts-title">
            Untagged Class Alerts
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Past classes that tutors haven&apos;t tagged Present / No Show / Cancelled. These lock student
            credits — tag them directly from this panel or follow up with the tutor.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(parseInt(e.target.value, 10))}
            className="border border-slate-300 rounded-md text-sm px-3 py-1.5 bg-white"
            data-testid="days-back-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary tile */}
      <Card
        className={cn(
          "border",
          total === 0
            ? "border-emerald-200 bg-emerald-50/50"
            : "border-rose-200 bg-rose-50/50"
        )}
        data-testid="alert-summary-card"
      >
        <CardContent className="pt-5 pb-5 flex items-start gap-3">
          {total === 0 ? (
            <>
              <span className="text-2xl">✅</span>
              <div className="text-sm text-emerald-900">
                <strong>All caught up.</strong> No past classes need tagging in the last {daysBack} days.
              </div>
            </>
          ) : (
            <>
              <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
              <div className="text-sm text-rose-900">
                <strong>{total} untagged class{total === 1 ? "" : "es"} require action.</strong>{" "}
                Affecting <strong>{data?.unique_tutors || 0} tutor{(data?.unique_tutors || 0) === 1 ? "" : "s"}</strong>.
                Use <span className="font-medium">Quick Tag</span> on a row to resolve without leaving this page.
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {loading && !data ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <Tabs defaultValue="all" className="space-y-4">
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">All Classes ({total})</TabsTrigger>
            <TabsTrigger value="by-tutor" data-testid="tab-by-tutor">By Tutor ({summary.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-2">
            {items.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-500">No untagged classes 🎉</CardContent></Card>
            ) : (
              items.map((it) => {
                const sev = severityForDays(it.days_overdue);
                const deepLink = [
                  "/schedule",
                  "?",
                  it.tutor_id ? `tutor_id=${encodeURIComponent(it.tutor_id)}&` : "",
                  `week_of=${encodeURIComponent(it.date)}&`,
                  `highlight=${encodeURIComponent(it.slot_id)}`,
                ].join("");
                return (
                  <Card
                    key={it.slot_id}
                    className="border-slate-200 hover:shadow-sm transition"
                    data-testid={`untagged-row-${it.slot_id}`}
                  >
                    <CardContent className="pt-4 pb-4 flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-2 min-w-[180px]">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="text-sm font-medium text-slate-900">{it.date} · {it.time}</div>
                          <div className="text-xs text-slate-500">{it.duration_minutes} min · {it.status}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 min-w-[200px]">
                        <User className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="text-sm text-indigo-600">{it.student_name}</div>
                          <div className="text-xs text-slate-500">Student</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 min-w-[180px]">
                        <GraduationCap className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="text-sm text-slate-900">{it.tutor_name}</div>
                          <div className="text-xs text-slate-500">Tutor</div>
                        </div>
                      </div>
                      <Badge variant="outline" className={cn("text-xs ml-auto", sev.className)}>
                        {it.days_overdue}d overdue · {sev.label}
                      </Badge>
                      <div className="flex items-center gap-2">
                        <QuickTagMenu
                          slot={it}
                          onTagged={tagSlot}
                          busy={busyId === it.slot_id}
                        />
                        <Link
                          to={deepLink}
                          className="text-slate-500 hover:text-indigo-700 inline-flex items-center"
                          title="Open in schedule"
                          data-testid={`open-schedule-link-${it.slot_id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="by-tutor" className="space-y-3">
            {summary.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-500">No untagged classes 🎉</CardContent></Card>
            ) : (
              summary.map((row) => {
                const tutorItems = items.filter((i) => i.tutor_id === row.tutor_id);
                // Open the tutor's schedule on the week of their oldest untagged class
                // (untagged classes are in the past, so the current week would look empty).
                const oldest = tutorItems.map((i) => i.date).filter(Boolean).sort()[0];
                return (
                  <Card
                    key={row.tutor_id || "unknown"}
                    className="border-slate-200 hover:shadow-sm transition"
                    data-testid={`tutor-row-${row.tutor_id || "unknown"}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                            <GraduationCap className="w-4 h-4 text-slate-500" />
                            <span>{row.tutor_name}</span>
                            <Badge
                              variant="outline"
                              className={cn("text-xs", severityForDays(row.max_days_overdue).className)}
                            >
                              {row.count} class{row.count === 1 ? "" : "es"} pending
                            </Badge>
                          </CardTitle>
                          <p className="text-xs text-slate-500 mt-1">
                            Affecting {row.unique_students} student{row.unique_students === 1 ? "" : "s"}:{" "}
                            {row.students_affected.slice(0, 4).join(", ")}
                            {row.students_affected.length > 4 && ` +${row.students_affected.length - 4} more`}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-slate-500">Oldest</div>
                          <div className="text-sm font-semibold text-rose-700">
                            {row.max_days_overdue} day{row.max_days_overdue === 1 ? "" : "s"}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-2">
                      {/* Inline per-class quick-tag list */}
                      {tutorItems.length > 0 && (
                        <div className="rounded-md border border-slate-200 bg-slate-50/40 divide-y divide-slate-200">
                          {tutorItems.slice(0, 6).map((it) => (
                            <div
                              key={it.slot_id}
                              className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                              data-testid={`tutor-row-item-${it.slot_id}`}
                            >
                              <span className="text-slate-700">
                                {it.date} · {it.time} — <span className="text-indigo-700">{it.student_name}</span>
                              </span>
                              <QuickTagMenu
                                slot={it}
                                onTagged={tagSlot}
                                busy={busyId === it.slot_id}
                              />
                            </div>
                          ))}
                          {tutorItems.length > 6 && (
                            <div className="px-3 py-2 text-xs text-slate-500 italic">
                              +{tutorItems.length - 6} more in the &quot;All Classes&quot; tab
                            </div>
                          )}
                        </div>
                      )}
                      <Link
                        to={
                          row.tutor_id
                            ? `/schedule?tutor_id=${encodeURIComponent(row.tutor_id)}${oldest ? `&week_of=${encodeURIComponent(oldest)}` : ""}`
                            : "/schedule"
                        }
                        className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                        data-testid={`open-schedule-${row.tutor_id || "unknown"}`}
                      >
                        Open {row.tutor_name}&apos;s Schedule <ChevronRight className="w-4 h-4" />
                      </Link>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
